import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUsers, FiAward, FiClock, FiHeart, FiShield, FiTrendingUp } = FiIcons;

const About = () => {
  const stats = [
    { number: '5000+', label: 'Happy Customers' },
    { number: '10+', label: 'Years Experience' },
    { number: '50+', label: 'Trained Professionals' },
    { number: '99%', label: 'Satisfaction Rate' }
  ];

  const values = [
    {
      icon: FiHeart,
      title: 'Customer First',
      description: 'We put our customers at the heart of everything we do, ensuring their satisfaction is our top priority.'
    },
    {
      icon: FiShield,
      title: 'Trust & Reliability',
      description: 'Our team is fully insured, bonded, and background-checked for your peace of mind.'
    },
    {
      icon: FiAward,
      title: 'Quality Excellence',
      description: 'We maintain the highest standards of quality in every cleaning service we provide.'
    },
    {
      icon: FiTrendingUp,
      title: 'Continuous Improvement',
      description: 'We constantly evolve our methods and training to deliver better results.'
    }
  ];

  const team = [
    {
      name: 'Sarah Mitchell',
      role: 'Founder & CEO',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b5bc?w=300&h=300&fit=crop&crop=face',
      description: 'With over 15 years in the cleaning industry, Sarah founded CleanPro to provide exceptional service.'
    },
    {
      name: 'David Rodriguez',
      role: 'Operations Manager',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
      description: 'David ensures our operations run smoothly and our team delivers consistent quality.'
    },
    {
      name: 'Emily Chen',
      role: 'Quality Supervisor',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
      description: 'Emily oversees quality control and training programs for our cleaning professionals.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
                About
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {' '}CleanPro
                </span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Since 2014, we've been dedicated to transforming spaces and exceeding expectations. 
                Our mission is to provide professional, reliable, and eco-friendly cleaning services 
                that give you more time to focus on what matters most.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
                  <span className="text-lg font-medium text-gray-700">Family Owned</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiShield} className="w-6 h-6 text-blue-600" />
                  <span className="text-lg font-medium text-gray-700">Fully Insured</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=600&fit=crop"
                alt="Our team at work"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-gray-900">Our Story</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  CleanPro was founded in 2014 by Sarah Mitchell, who recognized the need for 
                  reliable, professional cleaning services in our community. Starting with just 
                  a small team and a big vision, we've grown to become one of the most trusted 
                  cleaning companies in the area.
                </p>
                <p>
                  What sets us apart is our commitment to quality, reliability, and customer 
                  satisfaction. We believe that a clean environment contributes to better health, 
                  productivity, and overall well-being. That's why we use eco-friendly products 
                  and employ trained professionals who take pride in their work.
                </p>
                <p>
                  Today, we serve hundreds of satisfied customers across residential and commercial 
                  sectors, and we continue to grow while maintaining our core values of excellence 
                  and integrity.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=500&fit=crop"
                alt="Our journey"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These core values guide everything we do and shape how we serve our customers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600">
              The dedicated professionals behind CleanPro's success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white">Our Mission</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
              To provide exceptional cleaning services that enhance the quality of life for our 
              customers while maintaining the highest standards of professionalism, reliability, 
              and environmental responsibility. We strive to be the cleaning company you can 
              trust completely.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <SafeIcon icon={FiHeart} className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Care</h3>
                <p className="text-blue-100">We care about your space as if it were our own</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiShield} className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Trust</h3>
                <p className="text-blue-100">Building lasting relationships through reliability</p>
              </div>
              <div className="text-center">
                <SafeIcon icon={FiAward} className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Excellence</h3>
                <p className="text-blue-100">Delivering superior results every single time</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;